/**
 * Quem usa este telefone, e-mail, endereço ou perfil — a pergunta que a lista só insinua.
 *
 * Dois donos no mesmo recurso é o achado: um telefone compartilhado entre uma pessoa e uma empresa
 * costuma ser o vínculo que ninguém declarou. Por isso a tela abre já com a lista de donos e um
 * aviso quando há mais de um — em vez de tratar dois como se fosse igual a um.
 */
import { ArrowLeft, Building2, Home, type LucideIcon, Map, MapPin, User, Users } from 'lucide-react';
import { lazy, Suspense, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Chip, Spinner } from '@/components/ui/misc';
import { ModalMapa } from '@/components/crud/ModalMapa';
import { Pagina } from '@/components/ui/pagina';
import { api, type ContatoEnderecoCad } from '@/lib/api';
import { rotaEntidade } from '@/lib/recursos';

/** Carregado SOB DEMANDA para quebrar um ciclo de importação: o grafo puxa o `DrawerNoGrafo`, que
 * lê o registro de recursos — que é quem importa este arquivo. Importado no topo, o ciclo fechava
 * antes de o registro existir e a aplicação subia em branco («Cannot access 'RECURSOS' before
 * initialization»), sem nada na tela que dissesse o motivo. */
const PainelGrafo = lazy(() =>
  import('@/components/crud/paineis/PainelGrafo').then((m) => ({ default: m.PainelGrafo })),
);

type Dono = { owner_tipo: string; owner_id: string; nome: string };

const ICONE: Record<string, LucideIcon> = {
  pessoa: User,
  empresa: Building2,
  condominio: Home,
  imovel: MapPin,
};

const ROTULO_TIPO: Record<string, string> = {
  pessoa: 'Pessoa',
  empresa: 'Empresa',
  condominio: 'Condomínio',
  imovel: 'Imóvel',
};

export function PainelQuemUsa({
  recurso,
  item,
  icone: Icone,
}: {
  recurso: string;
  item: { id: string; rotulo: string; total_vinculos?: number } & Record<string, unknown>;
  icone: LucideIcon;
}) {
  const [donos, setDonos] = useState<Dono[] | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [mapaAberto, setMapaAberto] = useState(false);
  const navegar = useNavigate();

  // Endereço tem lugar no mundo: ver no mapa é a leitura mais direta que existe dele.
  const ehEndereco = recurso === 'enderecos';
  // Rede social ainda não entra na travessia (não é ponte de correlação hoje).
  const temGrafo = ['telefones', 'emails', 'enderecos'].includes(recurso);

  useEffect(() => {
    let vivo = true;
    setCarregando(true);
    void api
      .get<Dono[]>(`/cadastro/auxiliares/${recurso}/${item.id}/vinculos`)
      .then((r) => {
        if (!vivo) return;
        setCarregando(false);
        if (r.ok) setDonos(r.dados);
        else toast.error(r.erro);
      });
    return () => {
      vivo = false;
    };
  }, [recurso, item.id]);

  const compartilhado = (donos?.length ?? 0) > 1;

  return (
    <Pagina titulo={item.rotulo} sobretitulo="Auxiliar" icone={Icone}>
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="fantasma" onClick={() => navegar(-1)}>
            <ArrowLeft /> Voltar
          </Button>
          {ehEndereco && (
            <Button variant="secundario" onClick={() => setMapaAberto(true)}>
              <Map /> Ver no mapa
            </Button>
          )}
        </div>

        {ehEndereco && (
          <ModalMapa
            endereco={
              mapaAberto ? (item as unknown as ContatoEnderecoCad['endereco']) : null
            }
            onFechar={() => setMapaAberto(false)}
          />
        )}

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="size-4 text-accent" />
              Quem usa
              {compartilhado && (
                <Chip className="border-accent/40 text-accent">
                  compartilhado por {donos?.length}
                </Chip>
              )}
            </CardTitle>
            <p className="text-[13px] leading-[1.5] text-text-secondary">
              {compartilhado
                ? 'Mais de um dono no mesmo registro. É assim que aparece um vínculo que ninguém declarou.'
                : 'Os cadastros que apontam para este registro.'}
            </p>
          </CardHeader>
          <CardContent className="flex flex-col">
            {carregando ? (
              <Spinner />
            ) : !donos?.length ? (
              <p className="tipo-corpo-pequeno text-text-tertiary">
                Ninguém usa este registro. Ele sobrou de um vínculo desfeito ou de uma importação.
              </p>
            ) : (
              donos.map((d) => {
                const IconeDono = ICONE[d.owner_tipo] ?? Users;
                return (
                  <div
                    key={`${d.owner_tipo}-${d.owner_id}`}
                    className="flex items-center gap-3 border-t border-border py-3 first:border-t-0"
                  >
                    <IconeDono className="size-4 shrink-0 text-text-tertiary" />
                    <Link
                      to={rotaEntidade(d.owner_tipo, d.owner_id)}
                      className="flex-1 text-[15px] text-text hover:text-accent hover:underline"
                    >
                      {d.nome}
                    </Link>
                    <span className="tipo-corpo-pequeno text-text-tertiary">
                      {ROTULO_TIPO[d.owner_tipo] ?? d.owner_tipo}
                    </span>
                  </div>
                );
              })
            )}
          </CardContent>
        </Card>

        {/* O grafo com ESTE recurso na âncora: os donos em volta e o que os liga entre si depois.
            Ancorar numa pessoa responderia «com quem ela se parece»; aqui a pergunta é «quem usa
            isto e o que mais eles têm em comum». */}
        {temGrafo && (
          <Suspense fallback={<Spinner />}>
            <PainelGrafo
              ownerTipo="pessoa"
              ownerId={item.id}
              base={`/cadastro/auxiliares/${recurso}/${item.id}/grafo`}
            />
          </Suspense>
        )}

        {/* Os campos crus do registro, para quem precisa conferir o que está gravado. */}
        <Card>
          <CardHeader>
            <CardTitle>Registro</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2">
              {Object.entries(item)
                .filter(
                  ([k, v]) =>
                    !['id', 'rotulo', 'vinculos', 'total_vinculos'].includes(k) &&
                    v !== null &&
                    v !== '',
                )
                .map(([k, v]) => (
                  <div key={k} className="contents">
                    <dt className="tipo-rotulo self-center">{k.replace(/_/g, ' ')}</dt>
                    <dd className="tipo-dado text-text">{String(v)}</dd>
                  </div>
                ))}
            </dl>
          </CardContent>
        </Card>
      </div>
    </Pagina>
  );
}
